import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { config } from '@app/core/app-config';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { Logger } from '@utils';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { captureException } from 'src/sentry';
import { PresenceInfo, PresenceType, PusherService } from '../pusher/pusher.service';

@Injectable({
  providedIn: 'root',
})
export class PresenceService implements OnDestroy {
  // State subject for active users and their presence info
  private activeUsers$ = new BehaviorSubject<Map<string, PresenceInfo>>(new Map());

  // Map to track timeout handlers for each user
  private userTimeouts = new Map<string, NodeJS.Timeout>();
  private readonly PRESENCE_TIMEOUT_MS = 300000; // 5 minutes for human users
  private readonly AI_PRESENCE_TIMEOUT_MS = 60000; // 1 minute for AI users

  // Utility subject for cleanup
  private destroy$ = new Subject<void>();

  private currentUserId: string;

  constructor(
    private pusherService: PusherService,
    private authService: AuthenticationService,
    private http: HttpClient,
    private segmentIoService: SegmentIoService
  ) {
    this.currentUserId = this.authService.getUserDetails()?.id;
    // Subscribe to presence updates from Pusher
    this.pusherService.presenceUpdate$
      .pipe(
        takeUntil(this.destroy$),
        // Only process presence updates from other users or AI
        filter((payload) => String(payload.user_id) !== String(this.currentUserId) || payload.is_ai)
      )
      .subscribe(this.handlePresenceUpdate.bind(this));
  }

  private handlePresenceUpdate(payload: PresenceInfo): void {
    console.log('Presence: Received presence update', payload);

    if (!payload || !payload.user_id) {
      console.error('Presence: Received invalid presence payload', payload);
      this.segmentIoService.track(SegmentEvent.InboxPresenceReceivedInvalidPayload, { payload });
      captureException(SegmentEvent.InboxPresenceReceivedInvalidPayload);
      return;
    }

    const presenceInfo: PresenceInfo = {
      ...payload,
      created_at: payload.created_at instanceof Date ? payload.created_at : new Date(payload.created_at),
    };

    // Update active users map
    const newMap = new Map(this.activeUsers$.value);

    if (payload.type === PresenceType.Idle) {
      // Remove user if idle
      newMap.delete(payload.user_id);
      this.clearUserTimeout(payload.user_id);
    } else {
      // Add or update user
      newMap.set(payload.user_id, presenceInfo);
      // Use specific timeout for AI, default for humans
      const timeoutDuration = payload.is_ai ? this.AI_PRESENCE_TIMEOUT_MS : this.PRESENCE_TIMEOUT_MS;
      this.resetUserTimeout(payload.user_id, timeoutDuration);
    }

    this.activeUsers$.next(newMap);
  }

  private resetUserTimeout(userId: string, duration: number): void {
    // Clear existing timeout if any
    this.clearUserTimeout(userId);

    // Set new timeout
    const timeout = setTimeout(() => {
      const newMap = new Map(this.activeUsers$.value);
      newMap.delete(userId);
      this.activeUsers$.next(newMap);
      this.userTimeouts.delete(userId);
    }, duration);

    this.userTimeouts.set(userId, timeout);
  }

  private clearUserTimeout(userId: string): void {
    const timeout = this.userTimeouts.get(userId);
    if (timeout) {
      clearTimeout(timeout);
      this.userTimeouts.delete(userId);
    }
  }

  getAIActingInThread(threadId: string): Observable<PresenceInfo | null> {
    return this.activeUsers$.pipe(
      map((usersMap) => {
        return Array.from(usersMap.values()).find((user) => user.is_ai && user.resource_id === threadId) || null;
      })
    );
  }

  // Public methods to get filtered lists of users
  getUsersViewingThreadList(): Observable<PresenceInfo[]> {
    return this.activeUsers$.pipe(
      map((usersMap) => {
        const users: PresenceInfo[] = [];
        usersMap.forEach((user) => {
          if (user.type === PresenceType.InboxViewingThreadList) {
            users.push(user);
          }
        });
        return users;
      })
    );
  }

  getUsersViewingThread(threadId: string): Observable<PresenceInfo[]> {
    return this.activeUsers$.pipe(
      map((usersMap) => {
        const users: PresenceInfo[] = [];
        usersMap.forEach((user) => {
          if (user.type === PresenceType.InboxViewingThread && user.resource_id === threadId) {
            users.push(user);
          }
        });
        return users;
      })
    );
  }

  getUsersViewingThreads(): Observable<Map<string, PresenceInfo[]>> {
    return this.activeUsers$.pipe(
      map((usersMap) => {
        const threadViewers = new Map<string, PresenceInfo[]>();

        usersMap.forEach((user) => {
          if (
            (user.type === PresenceType.InboxViewingThread || user.type === PresenceType.InboxTypingMessage) &&
            user.resource_id
          ) {
            const existing = threadViewers.get(user.resource_id) || [];
            threadViewers.set(user.resource_id, [...existing, user]);
          }
        });

        return threadViewers;
      })
    );
  }

  getUsersTypingInThread(threadId: string): Observable<PresenceInfo[]> {
    return this.activeUsers$.pipe(
      map((usersMap) => {
        const users: PresenceInfo[] = [];
        usersMap.forEach((user) => {
          if (user.type === PresenceType.InboxTypingMessage && user.resource_id === threadId) {
            users.push(user);
          }
        });
        return users;
      })
    );
  }

  // todo: if idle, remove from tracking

  // Send presence update to the server
  // We do not send these for impersonated logins
  sendPresenceUpdate(
    type: PresenceType,
    resourceId: string | null,
    resourceType: string
  ): Observable<{ success: boolean }> {
    if (this.authService.isCS()) {
      Logger.log('Presence: Not sending presence update for impersonated user');
      return of({ success: false });
    }

    return this.http.post<{ success: boolean }>(`${config.API_URL}/presence`, {
      type,
      resource_id: resourceId,
      resource_type: resourceType,
    });
  }

  ngOnDestroy(): void {
    // Clear all timeouts
    this.userTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.userTimeouts.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }
}
